import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import DashboardLayout from './Layout/DashboardLayout';
import DashboardLogin from './Auth/DashboardLogin';
import ProtectedRoute from './Auth/ProtectedRoute';

// Dashboard Pages

import SciReportPage from './SciReport/SciReportPage';
import ImageCheckPage from './ImageCheck/ImageCheckPage';
import Deepfake from './Deepfake/Deepfake';
import VideoSectionPage from './AvLibrary/VideoSectionPage';
import EDiscoveryPage from './MyNoScam/EDiscoveryPage';
import FeedbackPage from './MyNoScam/FeedbackPage';
import DashboardSupport from './Support/DashboardSupport';
import AccountManagement from './Account/AccountManagement';
import Billing from './Billing/Billing';
import NotificationsHub from './Notifications/NotificationsHub';
import AdminDashboard from './Admin/AdminDashboard';
import RequireAdmin from './Admin/RequireAdmin';
import FraudAlertsPage from './FraudAlerts/FraudAlertsPage';
import SocialMediaIQPage from './SocialMediaIQ/SocialMediaIQPage';
import AbusePlaybookPage from './SocialMediaIQ/AbusePlaybookPage';
import FraudAlertDetailPage from './FraudAlerts/FraudAlertDetailPage';
import ReportPage from './Report/ReportPage';
import ThreatBattlespacePage from './RedFlagAlerts/ThreatBattlespacePage';
import FileAComplaintPage from './RedFlagAlerts/FileAComplaintPage';
import PreventionResourcesPage from './PreventionResources/PreventionResourcesPage';

// New pages
import HotScamDetailPage from './HotScams/HotScamDetailPage';
import PodcastPage from './AvLibrary/PodcastPage';

// MoSAIC sub-pages (standalone routes)
import PigButcheringCryptoAssessment from './MosaicPSYOPS/PigButcheringCryptoAssessment';
import CatfishingRomanceAssessment from './MosaicPSYOPS/CatfishingRomanceAssessment';
import MosaicPSYOPSExplainedPage from './MosaicPSYOPS/MosaicPSYOPSExplainedPage';
import CyberFraudAlertExplainedPage from './FraudAlerts/CyberFraudAlertExplainedPage';
import AssessmentResultsPage from './MosaicPSYOPS/AssessmentResultsPage';
import TheDayAfterPage from './MosaicPSYOPS/TheDayAfterPage';

// Learn More Pages
import FraudHQPage from './FraudHQ/FraudHQPage';
import PhishingLearnMorePage from './Phishing/PhishingLearnMorePage';
import CryptocurrencyLearnMorePage from './Cryptocurrency/CryptocurrencyLearnMorePage';
import TechSupportLearnMorePage from './TechSupport/TechSupportLearnMorePage';
import CatfishingLearnMorePage from './Catfishing/CatfishingLearnMorePage';
import WorkFromHomeLearnMorePage from './WorkFromHome/WorkFromHomeLearnMorePage';
import ExtortionLearnMorePage from './Extortion/ExtortionLearnMorePage';
import LotterySweepstakeLearnMorePage from './LotterySweepstake/LotterySweepstakeLearnMorePage';
import LawEnforcementImpersonationLearnMorePage from './LawEnforcementImpersonation/LawEnforcementImpersonationLearnMorePage';
import FuneralCryptoATMLearnMorePage from './FuneralCryptoATM/FuneralCryptoATMLearnMorePage';
import GrandparentLearnMorePage from './Grandparent/GrandparentLearnMorePage';
import GovernmentImpersonationLearnMorePage from './GovernmentImpersonation/GovernmentImpersonationLearnMorePage';
import FuneralLearnMorePage from './Funeral/FuneralLearnMorePage';
import CryptoATMLearnMorePage from './CryptoATM/CryptoATMLearnMorePage';
import CharityLearnMorePage from './Charity/CharityLearnMorePage';
import RedFlagPigButcheringLearnMorePage from './RedFlagAlert/RedFlagPigButcheringLearnMorePage';

/**
 * Dashboard landing. Every subscriber gets the same access under the single
 * tier, so this no longer branches on tier — it was previously Ares →
 * fraud-alerts, Athena → account-management. ProtectedRoute on the target route
 * handles anyone without an active subscription, so no subscription check is
 * needed here and the redirect can be unconditional.
 */
const HomeRedirect = () => <Navigate to="/dashboard/fraud-alerts" replace />;

const DashboardApp = () => {
  return (
    <Routes>
      {/* Login Page - Outside of dashboard layout */}
      <Route path="login" element={<DashboardLogin />} />

      {/* Dashboard Routes with Dashboard Layout - All require token authentication */}
      {/* The gate wraps the layout, not just the pages inside it. Gating only the
          children painted the whole dashboard shell — nav, sidebar, heading —
          while the subscription check was still in flight, so arriving from the
          marketing site flashed a dashboard that looked unsubscribed. The inner
          ProtectedRoutes stay as-is; they pass instantly once this one has. */}
      <Route path="/*" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
          {/* Landing: the Fraud Alerts hub for every subscriber */}
          <Route index element={<HomeRedirect />} />

          {/* CyberFraud Alert hub — default home (IC3/SSA/IRS/FTC tabs + ScamWire) */}
          <Route path="fraud-alerts" element={
            <ProtectedRoute>
              <FraudAlertsPage />
            </ProtectedRoute>
          } />
          <Route path="cyberfraud-alert-explained" element={
            <ProtectedRoute>
              <CyberFraudAlertExplainedPage />
            </ProtectedRoute>
          } />

          {/* Social Media IQ — platform threat matrix; the Abuse Playbook tactics are their own page */}
          <Route path="social-media-iq" element={
            <ProtectedRoute>
              <SocialMediaIQPage />
            </ProtectedRoute>
          } />
          <Route path="abuse-playbook" element={
            <ProtectedRoute>
              <AbusePlaybookPage />
            </ProtectedRoute>
          } />
          <Route path="fraud-alerts/:id" element={
            <ProtectedRoute>
              <FraudAlertDetailPage />
            </ProtectedRoute>
          } />
          {/* Threat Battlespace: the safe-surfing tips (same list as the ribbon) */}
          <Route path="threat-battlespace" element={
            <ProtectedRoute>
              <ThreatBattlespacePage />
            </ProtectedRoute>
          } />
          <Route path="safe-surfing-tips" element={<Navigate to="/dashboard/threat-battlespace" replace />} />
          {/* File a Complaint: links to the four agencies' complaint sites */}
          <Route path="file-a-complaint" element={
            <ProtectedRoute>
              <FileAComplaintPage />
            </ProtectedRoute>
          } />
          {/* Prevention Resources splash - also the desktop ribbon's target */}
          <Route path="prevention-resources" element={
            <ProtectedRoute>
              <PreventionResourcesPage />
            </ProtectedRoute>
          } />
          {/* In-app scam report (form under construction) */}
          <Route path="report" element={
            <ProtectedRoute>
              <ReportPage />
            </ProtectedRoute>
          } />
          {/* My Devices now lives on the Account Management page; keep the old path working */}
          <Route path="my-devices" element={<Navigate to="/dashboard/account-management" replace />} />

          {/* Legacy scam detail pages (no longer linked from nav) */}
          <Route path="red-flag-pig-butchering-learn-more" element={
            <ProtectedRoute>
              <RedFlagPigButcheringLearnMorePage />
            </ProtectedRoute>
          } />
          {/* New-layout scam detail pages (Information Hub refactor) */}
          <Route path="hot-scams/:slug" element={
            <ProtectedRoute>
              <HotScamDetailPage />
            </ProtectedRoute>
          } />
          <Route path="sci-report" element={
            <ProtectedRoute>
              <SciReportPage />
            </ProtectedRoute>
          } />
          {/* Legacy path — keep old links, bookmarks and notifications working */}
          <Route path="sci-overview" element={<Navigate to="/dashboard/sci-report" replace />} />
          <Route path="deepfake" element={
            <ProtectedRoute>
              <Deepfake />
            </ProtectedRoute>
          } />
          <Route path="image-check" element={
            <ProtectedRoute>
              <ImageCheckPage />
            </ProtectedRoute>
          } />

          {/* MoSAIC PSYOPS section: every page is a standalone route; the old
              hub / splash path redirects to Explained. */}
          <Route path="mosaic-psyops" element={<Navigate to="/dashboard/mosaic-psyops-explained" replace />} />
          <Route path="mosaic-psyops-explained" element={
            <ProtectedRoute>
              <MosaicPSYOPSExplainedPage />
            </ProtectedRoute>
          } />
          {/* Behavioral screenings — page names match their titles; each has a
              shared results page. Pre-refactor paths redirect below. */}
          <Route path="pig-butchering-crypto" element={
            <ProtectedRoute>
              <PigButcheringCryptoAssessment />
            </ProtectedRoute>
          } />
          <Route path="pig-butchering-crypto/results" element={
            <ProtectedRoute>
              <AssessmentResultsPage slug="pig-butchering-crypto" />
            </ProtectedRoute>
          } />
          <Route path="catfishing-romance" element={
            <ProtectedRoute>
              <CatfishingRomanceAssessment />
            </ProtectedRoute>
          } />
          <Route path="catfishing-romance/results" element={
            <ProtectedRoute>
              <AssessmentResultsPage slug="catfishing-romance" />
            </ProtectedRoute>
          } />
          <Route path="the-day-after" element={
            <ProtectedRoute>
              <TheDayAfterPage />
            </ProtectedRoute>
          } />

          {/* Legacy MoSAIC paths — keep old links and bookmarks working.
              The old /predator-dna playbook path has no route. */}
          <Route path="pig-butchering-assessment" element={<Navigate to="/dashboard/pig-butchering-crypto" replace />} />
          <Route path="catfishing-assessment" element={<Navigate to="/dashboard/catfishing-romance" replace />} />
          <Route path="day-after" element={<Navigate to="/dashboard/the-day-after" replace />} />

          {/* FBI \ IC3 Section */}
          {/* Notifications hub — available to any authenticated dashboard user */}
          <Route path="notifications" element={<NotificationsHub />} />
          {/* Admin (role-guarded). Published IC3 alerts surface on the PSAs page. */}
          <Route path="admin" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
          {/* Av Library Section. The static podcast route outranks the
              :section video route; an unknown section redirects to FBI / IC3. */}
          <Route path="av-library/podcast" element={
            <ProtectedRoute>
              <PodcastPage />
            </ProtectedRoute>
          } />
          <Route path="av-library/:section" element={
            <ProtectedRoute>
              <VideoSectionPage />
            </ProtectedRoute>
          } />
          <Route path="av-library" element={<Navigate to="/dashboard/av-library/fbi-ic3" replace />} />

          {/* Legacy Av Library paths — keep old links and bookmarks working */}
          <Route path="video-vault" element={<Navigate to="/dashboard/av-library/fbi-ic3" replace />} />
          <Route path="podcast-channels" element={<Navigate to="/dashboard/av-library/podcast" replace />} />

          {/* My No / SCAM Section */}
          {/* Help, Trust Center, and Support accessible to both Athena and Ares tiers */}
          <Route path="ediscovery" element={
            <ProtectedRoute>
              <EDiscoveryPage />
            </ProtectedRoute>
          } />
          <Route path="feedback" element={
            <ProtectedRoute>
              <FeedbackPage />
            </ProtectedRoute>
          } />
          {/* Legacy path — ePilot became eDiscovery */}
          <Route path="help" element={<Navigate to="/dashboard/ediscovery" replace />} />
          <Route path="support" element={
            <ProtectedRoute>
              <DashboardSupport />
            </ProtectedRoute>
          } />

          {/* Learn More Pages (re-enabled for Hot Scams links) */}
          <Route path="fraud-hq" element={
            <ProtectedRoute>
              <FraudHQPage />
            </ProtectedRoute>
          } />
          <Route path="phishing-learn-more" element={
            <ProtectedRoute>
              <PhishingLearnMorePage />
            </ProtectedRoute>
          } />
          <Route path="cryptocurrency-learn-more" element={
            <ProtectedRoute>
              <CryptocurrencyLearnMorePage />
            </ProtectedRoute>
          } />
          <Route path="tech-support-learn-more" element={
            <ProtectedRoute>
              <TechSupportLearnMorePage />
            </ProtectedRoute>
          } />
          <Route path="catfishing-learn-more" element={
            <ProtectedRoute>
              <CatfishingLearnMorePage />
            </ProtectedRoute>
          } />
          <Route path="work-from-home-learn-more" element={
            <ProtectedRoute>
              <WorkFromHomeLearnMorePage />
            </ProtectedRoute>
          } />
          <Route path="government-impersonation-learn-more" element={
            <ProtectedRoute>
              <GovernmentImpersonationLearnMorePage />
            </ProtectedRoute>
          } />
          <Route path="extortion-learn-more" element={
            <ProtectedRoute>
              <ExtortionLearnMorePage />
            </ProtectedRoute>
          } />
          <Route path="lottery-sweepstake-learn-more" element={
            <ProtectedRoute>
              <LotterySweepstakeLearnMorePage />
            </ProtectedRoute>
          } />
          <Route path="law-enforcement-impersonation-learn-more" element={
            <ProtectedRoute>
              <LawEnforcementImpersonationLearnMorePage />
            </ProtectedRoute>
          } />
          <Route path="funeral-crypto-atm-learn-more" element={
            <ProtectedRoute>
              <FuneralCryptoATMLearnMorePage />
            </ProtectedRoute>
          } />
          <Route path="funeral-learn-more" element={
            <ProtectedRoute>
              <FuneralLearnMorePage />
            </ProtectedRoute>
          } />
          <Route path="crypto-atm-learn-more" element={
            <ProtectedRoute>
              <CryptoATMLearnMorePage />
            </ProtectedRoute>
          } />
          <Route path="charity-learn-more" element={
            <ProtectedRoute>
              <CharityLearnMorePage />
            </ProtectedRoute>
          } />
          <Route path="grandparent-learn-more" element={
            <ProtectedRoute>
              <GrandparentLearnMorePage />
            </ProtectedRoute>
          } />

          {/* Sensitive pages require additional authentication */}
          <Route path="account-management" element={
            <ProtectedRoute requireAdditionalAuth={true}>
              <AccountManagement />
            </ProtectedRoute>
          } />
          <Route path="billing" element={
            <ProtectedRoute requireAdditionalAuth={true}>
              <Billing />
            </ProtectedRoute>
          } />
        </Route>
      </Routes>
  );
};

export default DashboardApp;
